/**
 * "How many X" for any paginated report, from a descriptor instead of code.
 *
 * The endpoint surface this assistant sits on is large (~238 entries) but
 * unusually regular: around nineteen report families all expose the same
 * `search` / `report` / `summary` / `group` shape over the same filters
 * (`startDate`, `endDate`, `locationId`, `page`, `size`). Writing a bespoke
 * skill per report would mean nineteen near-identical answer functions, each an
 * opportunity to word a total slightly differently or forget that a period was
 * never applied.
 *
 * So counting is written once here and each report contributes a descriptor.
 * Adding a report becomes a data change of about ten lines, which is the same
 * property the knowledge registry already has for questions.
 *
 * Only counting is generic. Money, ageing and anything else whose meaning
 * differs per report stays a hand-written skill — `revenue` and
 * `outstandingReceivables` exist precisely because their *wording* carries
 * domain judgement that a template would flatten.
 */

import { countReportRows } from './requests/report-count-requests';
import { apiDateRange } from './requests/apiDates';
import { hasPeriodEnded } from './periodParser';
import { detectComparison, type ComparisonQuestion } from './comparisonQuestion';
import { answerCountsByMonth } from './reportCountsByMonth';
import { classifyLookup, isHowToOrActionRequest } from './lookupRouting';
import { coerceCount } from '../../requests/generic-requests';
import type { AssistantAudience } from './assistantCapabilities';
import type {
  KnowledgeAnswer,
  KnowledgeContext,
  KnowledgeLink,
  KnowledgeQuery,
} from './knowledgeRegistry';

export interface ReportDescriptor {
  id: string;
  audiences: AssistantAudience[];
  /** The paginated search endpoint whose rows are being counted. */
  searchEndpoint: string;
  /**
   * The thing being counted, singular. Pluralised with a trailing "s" unless
   * {@link nounPlural} says otherwise.
   */
  noun: string;
  nounPlural?: string;
  /**
   * How an account-wide answer opens. Defaults to "You have"/"You had", which
   * is wrong for an admin — the platform's invoices are not *theirs*, so an
   * admin skill reads "There are …" instead.
   *
   * Carries four forms because two things vary independently: the count picks
   * singular or plural, and the period picks tense. A closed window needs the
   * past ("There were 3,189 invoices last month"); an open one needs the
   * present ("There are 12 invoices so far this month").
   */
  subject?: SubjectForms;
  /** Phrasings that identify this report for the deterministic fallback. */
  synonyms: string[];
  /**
   * Concept groups for phrasings the synonym list cannot enumerate, passed
   * straight through to {@link KnowledgeQuery.requires}. Consulted only after
   * every synonym has failed, so it can rescue an unrecognised wording but
   * never steal a question from a skill that matched precisely.
   */
  requires?: string[][];
  /** Written for a planner choosing between skills. */
  description: string;
  example: string;
  /**
   * Whether `startDate` / `endDate` actually filter this endpoint.
   *
   * Load-bearing: when false the answer says "right now" instead of naming a
   * period. Claiming a window that the server ignored would be a wrong answer
   * dressed as a precise one, which is worse than a vaguer true one.
   */
  supportsPeriod: boolean;
  /** Whether `locationId` filters this endpoint. */
  supportsLocation: boolean;
  /** Filters that define the subset, e.g. an active-only status. */
  fixedParams?: Record<string, string | number>;
  /** Optional CTA link shown under the answer. */
  link?: KnowledgeLink;
  /**
   * Whether {@link link}'s page binds `startDate` / `endDate` from its URL, so
   * the link can open on the window that was counted rather than the page's
   * default one.
   */
  linkTakesPeriod?: boolean;
  /** Optional context after the counted noun, e.g. "in Current Inventory". */
  countContext?: string;
  /**
   * The words that name this report in a month comparison ("which month had
   * the most check-ins"). Such questions rarely repeat a count synonym, so
   * without this they fall through to the agent even though the report can
   * answer them month by month. Only honoured when {@link supportsPeriod}.
   */
  comparisonNouns?: RegExp;
  /**
   * Words that mean a sibling report is being asked about instead, e.g.
   * check-outs for the check-ins report, so "check-ins and check-outs" is
   * left to the interchanges report that counts both.
   */
  comparisonExcludes?: RegExp;
}

/*
 * A count answers "how many", never "how much". A month comparison that talks
 * about money or a payment outcome belongs to a money skill or the agent, even
 * when it also says "invoices".
 */
const NOT_A_COUNT =
  /\$|\b(revenue|amounts?|values?|money|dollars?|spend|spent|paid|pay|payments?|payouts?|earn\w*|income|costs?|charges?|charged|refund\w*|fees?|overages?|failed|declined|outstanding|owed?|owing|unpaid|overdue|disputed?)\b/i;

/** Whether this report can split its count by month (its search filters by date). */
const countsByMonth = (descriptor: ReportDescriptor, comparison: ComparisonQuestion | null): boolean =>
  descriptor.supportsPeriod && comparison?.dimension === 'month';

/**
 * Most locations a count will fan out over before it stops breaking down. Each
 * row is its own request, so a large portfolio would otherwise fire dozens of
 * calls to render one answer.
 */
const MAX_BREAKDOWN_REQUESTS = 12;

/*
 * Count endpoints in this codebase answer in three different shapes — a
 * paginated `{ count, content }`, a bare `{ count }` value object, and a plain
 * number as text. Reading only `body.count` would quietly return 0 for the
 * last, which is the worst possible failure for a count: a confident, wrong,
 * plausible answer. `coerceCount` already handles all three and is what the nav
 * badges use, so the assistant reconciles with them by construction.
 */
const readCount = (response: { body?: unknown; text?: unknown } | undefined): number =>
  coerceCount(response?.body) ?? coerceCount(response?.text) ?? 0;

/** Subject openers for both grammatical numbers and both tenses. */
export interface SubjectForms {
  present: { singular: string; plural: string };
  past: { singular: string; plural: string };
}

const PERSONAL_SUBJECT: SubjectForms = {
  present: { singular: 'You have', plural: 'You have' },
  past: { singular: 'You had', plural: 'You had' },
};

/**
 * Whether the window being reported on has already closed.
 *
 * Derived from the dates rather than from a list of period ids, so a period
 * added later gets the right tense without anyone remembering to classify it.
 * A report with no date filter is always "right now", so it is never past.
 */
const isClosedPeriod = (descriptor: ReportDescriptor, context: KnowledgeContext): boolean =>
  descriptor.supportsPeriod && hasPeriodEnded(context.period);

const pluralNoun = (descriptor: ReportDescriptor, count: number): string => {
  const plural = descriptor.nounPlural ?? `${descriptor.noun}s`;
  return count === 1 ? descriptor.noun : plural;
};

/** "over the last 12 months", or "right now" when the report has no date filter. */
const periodPhrase = (descriptor: ReportDescriptor, context: KnowledgeContext): string =>
  descriptor.supportsPeriod ? context.period.label : 'right now';

const countSentence = (
  descriptor: ReportDescriptor,
  subject: string,
  count: number,
  phrase: string,
): string =>
  // Optional context keeps generic count wording while letting a report make
  // the dataset explicit ("in Current Inventory") where that clarity matters.
  count === 0
    ? `${subject} no ${descriptor.nounPlural ?? `${descriptor.noun}s`}${descriptor.countContext ? ` ${descriptor.countContext}` : ''} ${phrase}.`
    : `${subject} **${count}** ${pluralNoun(descriptor, count)}${descriptor.countContext ? ` ${descriptor.countContext}` : ''} ${phrase}.`;

/** The account-wide opener, agreeing with both the count and the period. */
const accountSubject = (
  descriptor: ReportDescriptor,
  count: number,
  past: boolean,
): string => {
  const forms = descriptor.subject ?? PERSONAL_SUBJECT;
  const tense = past ? forms.past : forms.present;
  return count === 1 ? tense.singular : tense.plural;
};

/** The descriptor's link, opening on the counted window when its page takes one. */
const linkFor = (
  descriptor: ReportDescriptor,
  dates: { startDate?: string; endDate?: string },
): KnowledgeLink | undefined => {
  const { link } = descriptor;
  if (!link || !descriptor.linkTakesPeriod || !dates.startDate || !dates.endDate) return link;
  const params = new URLSearchParams({ startDate: dates.startDate, endDate: dates.endDate });
  return { ...link, path: `${link.path}${link.path.includes('?') ? '&' : '?'}${params.toString()}` };
};

/**
 * Builds the registry entry that answers "how many X" for one report.
 *
 * Mirrors the fan-out behaviour of the hand-written skills: one request for the
 * whole account, one per location for a small named set, and a single
 * account-wide request with an explanation once the set is too large to break
 * down without a burst of calls.
 */
export const buildReportCountQuery = (descriptor: ReportDescriptor): KnowledgeQuery => ({
  id: descriptor.id,
  audiences: descriptor.audiences,
  synonyms: descriptor.synonyms,
  requires: descriptor.requires,
  example: descriptor.example,
  description: descriptor.description,
  // One total cannot rank months, compare years or rank locations, so any
  // comparison this report cannot split by month goes to the agent instead of
  // being answered with a single, confidently irrelevant count. Nor can it
  // count a subset it has no filter for ("invoices with failed payments per
  // month"), which would otherwise reach it through the "how many invoices"
  // synonym and count every invoice.
  declines: (message) => {
    const comparison = detectComparison(message);
    return comparison !== null && (!countsByMonth(descriptor, comparison) || NOT_A_COUNT.test(message));
  },
  claims: descriptor.comparisonNouns
    ? (message) =>
        countsByMonth(descriptor, detectComparison(message)) &&
        descriptor.comparisonNouns!.test(message) &&
        !descriptor.comparisonExcludes?.test(message) &&
        !NOT_A_COUNT.test(message) &&
        classifyLookup(message) === null &&
        !isHowToOrActionRequest(message)
    : undefined,
  answer: async (context: KnowledgeContext): Promise<KnowledgeAnswer> => {
    const { targets, wholeAccount, period } = context;

    const comparison = detectComparison(context.message ?? '');
    if (comparison && countsByMonth(descriptor, comparison)) {
      return answerCountsByMonth(
        context,
        {
          noun: descriptor.noun,
          nounPlural: descriptor.nounPlural ?? `${descriptor.noun}s`,
          supportsLocation: descriptor.supportsLocation,
          periodRange: apiDateRange(period),
          linkFor: (range) => linkFor(descriptor, range),
          countFor: async (range, locationId) =>
            readCount(
              await countReportRows(descriptor.searchEndpoint, {
                ...range,
                fixedParams: descriptor.fixedParams,
                ...(locationId ? { locationId } : {}),
              }),
            ),
        },
        comparison,
      );
    }

    const phrase = periodPhrase(descriptor, context);
    const past = isClosedPeriod(descriptor, context);

    const dates = descriptor.supportsPeriod
      ? apiDateRange(period)
      : {};
    const base = { ...dates, fixedParams: descriptor.fixedParams };
    const link = linkFor(descriptor, dates);

    const countFor = async (locationId?: string): Promise<number> =>
      readCount(
        await countReportRows(descriptor.searchEndpoint, {
          ...base,
          ...(locationId && descriptor.supportsLocation ? { locationId } : {}),
        }),
      );

    // A report that cannot filter by location can only ever be answered for the
    // whole account, so it says so rather than implying the named location was
    // honoured.
    if (wholeAccount || !descriptor.supportsLocation) {
      const total = await countFor();
      return {
        text: countSentence(descriptor, accountSubject(descriptor, total, past), total, phrase),
        link,
      };
    }

    if (targets.length === 1) {
      const total = await countFor(targets[0].id);
      return {
        text: countSentence(
          descriptor,
          `**${targets[0].name}** ${past ? 'had' : 'has'}`,
          total,
          phrase,
        ),
        link,
      };
    }

    if (targets.length > MAX_BREAKDOWN_REQUESTS) {
      const total = await countFor();
      return {
        text:
          `${countSentence(descriptor, accountSubject(descriptor, total, past), total, phrase).slice(0, -1)} across every ` +
          `location. That is more than ${MAX_BREAKDOWN_REQUESTS} locations, so I have not ` +
          'broken it down — ask about one location for its own count.',
        link,
      };
    }

    const counts = await Promise.all(
      targets.map(async (target) => ({
        label: target.name,
        count: await countFor(target.id),
      })),
    );

    const total = counts.reduce((sum, entry) => sum + entry.count, 0);
    return {
      text: countSentence(
        descriptor,
        `Those ${targets.length} locations have`,
        total,
        phrase,
      ),
      breakdown: counts.map((entry) => ({ label: entry.label, value: String(entry.count) })),
      link,
    };
  },
});
